import React, { useCallback, useMemo, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import {
  Button,
  Dialog,
  DialogBody,
  DialogHeader,
  FileUpload,
  Icon,
  SkeletonTable,
  Tooltip,
  showToast,
} from '@repo/ui';
import { DataTable, DataTableColumn } from '@xtnd/web/shared/components';
import { dateUtil, fileUtil } from '@xtnd/web/shared';
import { DateFormat } from '@xtnd/web/shared/utils/format.js';

import ConfirmDialog from './ConfirmDialog';
import {
  useAttemptAttachmentDownloadUrl,
  useAttemptAttachmentsQuery,
  useRemoveAttemptAttachment,
  useUploadAttemptAttachment,
} from '../../queries/attemptAttachmentsQuery';
import { Attachment, MatterAttempt } from '../../types';

interface AttemptAttachmentsContainerProps {
  isOpen: boolean;
  onClose: () => void;
  matterId: string;
  clientId: string;
  attempt: MatterAttempt;
}

const MAX_BATCH_FILES = 12;

export const AttemptAttachmentsContainer: React.FC<AttemptAttachmentsContainerProps> = ({
  isOpen,
  onClose,
  matterId,
  clientId,
  attempt,
}) => {
  const sk = attempt.sk;
  const qc = useQueryClient();

  const [files, setFiles] = useState<File[]>([]);
  const [selectedDoc, setSelectedDoc] = useState<Attachment | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDownloading, setIsDownloading] = useState<string | null>(null);

  const {
    data: listResponse,
    isFetching,
    isLoading: isLoadingList,
    error,
  } = useAttemptAttachmentsQuery(matterId, clientId, sk, { enabled: isOpen });

  const { mutateAsync: uploadFile } = useUploadAttemptAttachment(matterId, clientId, sk);
  const { mutateAsync: removeFile, isPending: isRemoving } = useRemoveAttemptAttachment(
    matterId,
    clientId,
    sk
  );
  const getDownloadUrl = useAttemptAttachmentDownloadUrl(matterId, clientId, sk);

  const boundFiles = listResponse?.data ?? [];

  const handleView = useCallback(
    async (file: Attachment) => {
      try {
        setIsDownloading(file.key);
        const url = await getDownloadUrl(file.key);
        window.open(url, '_blank');
      } catch {
        showToast.error('Failed to open attachment');
      } finally {
        setIsDownloading(null);
      }
    },
    [getDownloadUrl]
  );

  const renderActionCell = useCallback(
    (file: Attachment) => {
      if (isDownloading === file.key) {
        return (
          <Icon
            name="FaSpinner"
            size="sm"
            className="cursor-pointer animate-spin text-xtnd-dark hover:text-xtnd-blue dark:text-xtnd-light"
          />
        );
      }
      return (
        <div className="flex gap-x-2">
          <Icon
            name="HiEye"
            title="Preview document"
            size="sm"
            className="cursor-pointer text-xtnd-dark hover:text-xtnd-blue dark:text-xtnd-light"
            onClick={() => handleView(file)}
          />
          <Icon
            name="FaTrash"
            title="Delete attachment"
            size="sm"
            className="!h-4 !w-4 text-red-500 hover:text-red-500/50 cursor-pointer"
            onClick={() => setSelectedDoc(file)}
          />
        </div>
      );
    },
    [isDownloading, handleView]
  );

  const columns: DataTableColumn<Attachment>[] = useMemo(
    () => [
      {
        key: 'fileName',
        header: 'File Name',
        renderCell: file => (
          <Tooltip
            content={file.fileName ?? ''}
            position="auto"
            className="rounded-md"
            contentClassName="min-w-[12rem] h-full"
          >
            <span className="text-sm text-gray-900 dark:text-gray-100 block">
              <div className="text-sm text-xtnd-dark dark:text-xtnd-light max-w-[12rem] truncate block">
                {file.fileName}
              </div>
            </span>
          </Tooltip>
        ),
      },
      {
        key: 'fileSize',
        header: 'Size',
        defaultWidth: 100,
        renderCell: file => (
          <div className="text-sm text-xtnd-dark dark:text-xtnd-light w-[80px]">
            {fileUtil.formatFileSize(file.fileSize)}
          </div>
        ),
      },
      {
        key: 'createdAt',
        header: 'Created At',
        renderCell: file => (
          <div className="text-sm text-xtnd-dark dark:text-xtnd-light">
            {dateUtil.formatDate(file.createdAt, DateFormat.US_WITH_TIME)}
          </div>
        ),
      },
      {
        key: 'action',
        header: 'Action',
        renderCell: file => (
          <div className="text-sm text-xtnd-dark dark:text-xtnd-light flex justify-center gap-3">
            {renderActionCell(file)}
          </div>
        ),
      },
    ],
    [renderActionCell]
  );

  const handleClose = useCallback(() => {
    setFiles([]);
    setSelectedDoc(null);
    onClose();
  }, [onClose]);

  const handleUpload = useCallback(async () => {
    if (files.length === 0) return;
    setIsUploading(true);
    const failures: string[] = [];
    for (const file of files) {
      try {
        await uploadFile(file);
      } catch {
        failures.push(file.name);
      }
    }
    setIsUploading(false);
    setFiles([]);

    qc.invalidateQueries({ queryKey: ['attempt-attachments', matterId, clientId, sk] });
    qc.invalidateQueries({ queryKey: ['matter', 'attempts', matterId, clientId] });

    if (failures.length > 0) {
      showToast.error(`Failed to upload: ${failures.join(', ')}`);
    }
  }, [files, uploadFile, qc, matterId, clientId, sk]);

  const handleDelete = useCallback(async () => {
    if (!selectedDoc) return;
    await removeFile(selectedDoc.key);
    setSelectedDoc(null);
  }, [selectedDoc, removeFile]);

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      size="xxl"
      showCloseButton={false}
      className="!max-h-[90vh] overflow-y-auto"
    >
      <DialogHeader className="sticky top-0 bg-white dark:bg-xtnd-dark-800 z-[51]">
        <div className="flex justify-between">
          <h3 className="text-lg font-semibold text-xtnd-dark dark:text-xtnd-light">
            Attempt Attachments
          </h3>
          <button
            type="button"
            className="absolute z-[52] top-3 right-3 inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-xtnd-blue focus:ring-offset-2 transition-colors dark:text-gray-300 dark:hover:text-gray-100 dark:hover:bg-xtnd-dark-700"
            onClick={handleClose}
            aria-label="Close dialog"
          >
            <Icon name="HiOutlineX" />
          </button>
        </div>
      </DialogHeader>

      <DialogBody className="space-y-4">
        {isLoadingList ? (
          <SkeletonTable rows={4} columns={4} />
        ) : (
          <div className="space-y-2 mb-2">
            <p className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light mb-1 flex flex-col">
              <span>Upload new files</span>
            </p>
            <FileUpload
              files={files}
              isMultiple
              disabled={isUploading}
              accept=".eml,.doc,.docx,.pdf"
              maxSize={20 * 1024 * 1024}
              onChange={setFiles}
              isDisplayUploadButton={false}
              maxFileNumber={MAX_BATCH_FILES}
            />

            <div className="flex justify-end mt-4">
              <Button
                variant="primary"
                leftIcon={<Icon name="HiUpload" size="sm" />}
                size="sm"
                disabled={files.length === 0 || isUploading}
                className="rounded-none"
                loading={isUploading}
                onClick={handleUpload}
              >
                Upload
              </Button>
            </div>

            <div className="mt-4 space-y-4">
              <div>
                <p className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light mb-2">
                  Uploaded Attachments
                </p>
                {boundFiles.length > 0 ? (
                  <DataTable
                    columns={columns}
                    data={{
                      data: boundFiles,
                      total: boundFiles.length,
                    }}
                    isLoading={isFetching}
                    rowKey="key"
                    stickyHeader
                    error={error}
                  />
                ) : (
                  <div className="text-sm text-xtnd-dark dark:text-xtnd-light border border-gray-200 dark:border-gray-700 rounded-lg p-4 justify-center">
                    <div className="flex flex-col items-center justify-center gap-2">
                      <Icon
                        name="HiDocumentReport"
                        size="sm"
                        className="text-gray-500 dark:text-xtnd-light"
                      />
                      <p className="text-sm text-gray-500 dark:text-xtnd-light">
                        No attachments yet
                      </p>
                    </div>
                  </div>
                )}
              </div>

              <ConfirmDialog
                isOpen={!!selectedDoc}
                title="Delete file?"
                message={`Delete ${selectedDoc?.fileName ?? ''}? This cannot be undone.`}
                confirmText="Delete"
                variant="destructive"
                isLoading={isRemoving}
                onConfirm={handleDelete}
                onCancel={() => setSelectedDoc(null)}
              />
            </div>
          </div>
        )}
      </DialogBody>
    </Dialog>
  );
};

export default AttemptAttachmentsContainer;
